import type {ExternalViewerConfig} from './handler-structures'

/**
 * Lets a viewer name the PDF its print dialog saves. Chromium takes the default file name from the
 * top-level page's title, and a viewer runs cross-origin inside this shell, so it cannot set that title
 * itself. It asks, and the shell shows the title only while the dialog is open.
 *
 * Wire contract (html-viewer `HtmlViewer.ts`):
 *   viewer → host  {action: 'setPrintTitle', requestId, title: string | null}
 *   host → viewer  {action: 'setPrintTitle', requestId}
 * A string sets the title; null restores the original. The host replies to every well-formed request,
 * so the viewer never waits on its timeout.
 */
export const SET_PRINT_TITLE = 'setPrintTitle'

const MAX_PRINT_TITLE_LENGTH = 200

/** The title the page had before the first request; held until a null request restores it. */
let originalTitle: string | undefined

/**
 * Answer a viewer's `setPrintTitle` request. Every viewer manager routes the action here with the
 * config of the iframe that asked; a request from an unknown frame is acknowledged but changes nothing.
 */
export function answerPrintTitle(e: MessageEvent, config: ExternalViewerConfig | undefined): void {
  const {requestId, title} = (e.data ?? {}) as {requestId?: unknown; title?: unknown}
  if (!e.source || typeof requestId !== 'string') return
  if (title !== null && typeof title !== 'string') return

  if (config) {
    if (title === null) {
      if (originalTitle !== undefined) document.title = originalTitle
      originalTitle = undefined
    } else {
      originalTitle ??= document.title
      document.title = title.trim().slice(0, MAX_PRINT_TITLE_LENGTH)
    }
  }
  ;(e.source as Window).postMessage({action: SET_PRINT_TITLE, requestId}, '*')
}
